/**
 * Visit plan — the schedule of the buildings this provider covers (ruling,
 * Isaiah Cotto, 2026-10-19: "providers see their assigned facilities'
 * schedule from the scheduling board").
 *
 * Two answers, from one read (`GET /emerge/day-sheet/schedule`):
 *   1. the open facility's plan: its open visits (a passed date that Scheduling
 *      has not closed is said, not hidden) and its recent completions;
 *   2. across every building the provider covers: the upcoming visits, soonest
 *      first, each building's next and last visit, and the last 30 days'
 *      completions.
 *
 * The server decides which buildings: the caller's own scope, which for a
 * provider is the buildings their coverage granted. This screen never names a
 * building the server did not. Three non-data answers each get their own words:
 *   403 DENY_NO_CAPABILITY  no building is assigned to this login yet (an
 *                           explanation, not an error wall)
 *   404 NOT_FOUND           the open facility is not one of theirs; the
 *                           cross-facility schedule is still shown
 *   failure                 the server was not reached or failed: Try again
 *
 * Read-only. Scheduling plans visits; a provider asks Scheduling to move one.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUpRight, CalendarCheck2, CalendarClock, CalendarDays } from "lucide-react";
import { Button } from "../../../components/new-components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/new-components/ui/table";
import { StateBlock as TideState } from "../../../components/tide";
import { closureSentence, closureWords, providerApi, relativeDay, shortDate } from "../providerApi";
import type { ProviderSchedule, ScheduledVisit } from "../providerApi";
import { Notice, Section, StatusBadge } from "./FieldPrimitives";
import type { BadgeTone } from "./FieldPrimitives";

type Outcome =
  | { kind: "ok"; data: ProviderSchedule; selectedMissing: boolean }
  | { kind: "unassigned" }
  | { kind: "failure"; status?: number; message: string };

const STATE_LABEL: Record<ScheduledVisit["state"], string> = {
  PLANNED: "Planned",
  CONFIRMED: "Confirmed",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};
const STATE_TONE: Record<ScheduledVisit["state"], BadgeTone> = {
  PLANNED: "amber",
  CONFIRMED: "green",
  COMPLETED: "indigo",
  CANCELLED: "neutral",
};

const bare = (code: string | undefined) => String(code || "").replace(/^AUTHZ_/, "");
const list = <T,>(v: T[] | undefined | null): T[] => (Array.isArray(v) ? v : []);

function When({ day }: { day: string | null }) {
  const rel = relativeDay(day);
  return (
    <span className="fw-sched-when">
      <strong>{day ? shortDate(day) : "Date not set"}</strong>
      {rel && <small>{rel}</small>}
    </span>
  );
}

function Who({ visit }: { visit: ScheduledVisit }) {
  return (
    <span className="fw-sched-who">
      {visit.provider_name || "No provider named"}
      {visit.is_yours && <StatusBadge tone="green">You</StatusBadge>}
    </span>
  );
}

function VisitItem({ visit, completed = false }: { visit: ScheduledVisit; completed?: boolean }) {
  return (
    <li>
      {completed ? <CalendarCheck2 aria-hidden="true" /> : <CalendarClock aria-hidden="true" />}
      <div>
        <div className="fw-sched-line">
          <When day={completed ? visit.completed_on : visit.planned_on} />
          <StatusBadge tone={STATE_TONE[visit.state] || "neutral"}>{STATE_LABEL[visit.state] || visit.state}</StatusBadge>
          {visit.passed && <StatusBadge tone="rose">Date passed · still open</StatusBadge>}
        </div>
        <p>
          {visit.purpose || "No purpose recorded"} · <Who visit={visit} />
        </p>
      </div>
    </li>
  );
}

export default function FieldSchedule({
  facilityId,
  onOpenFacility,
}: {
  facilityId: string;
  onOpenFacility: (id: string) => void;
}) {
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [loading, setLoading] = useState(true);
  const request = useRef(0);
  useEffect(() => () => { request.current += 1; }, []);

  const load = useCallback(async () => {
    const mine = ++request.current;
    setLoading(true);
    try {
      let res = await providerApi.getSchedule(facilityId || null);
      let selectedMissing = false;
      // The open building is not one of theirs: still show the schedule they do have.
      if (!res.success && res.status === 404 && facilityId) {
        selectedMissing = true;
        res = await providerApi.getSchedule(null);
      }
      if (mine !== request.current) return;
      if (res.success && res.data) setOutcome({ kind: "ok", data: res.data, selectedMissing });
      else if (bare(res.code) === "DENY_NO_CAPABILITY") setOutcome({ kind: "unassigned" });
      else setOutcome({ kind: "failure", status: res.status, message: res.message || res.error || "The schedule did not load." });
    } catch {
      if (mine !== request.current) return;
      setOutcome({ kind: "failure", message: "Could not reach the server, so the schedule is not shown." });
    } finally {
      if (mine === request.current) setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  if (!outcome) {
    return (
      <Section title="Visit plan" subtitle="Scheduling's plan for the facilities you cover.">
        <div className="fw-panel-content">
          <TideState kind="loading" shape="lines" label="Loading the visit plan…" busy={false} />
        </div>
      </Section>
    );
  }

  if (outcome.kind === "unassigned") {
    return (
      <Section title="Visit plan" subtitle="Scheduling's plan for the facilities you cover.">
        <div className="fw-empty" role="status">
          <CalendarDays aria-hidden="true" />
          <h3>No facility is assigned to you yet</h3>
          <p>
            Your visit plan lists the facilities you are assigned to cover. Scheduling records
            that assignment in eMerge, and your access follows once your login is linked to your
            provider record. If you expected to see a facility here, ask Scheduling to check your
            assignment and your login link.
          </p>
        </div>
      </Section>
    );
  }

  if (outcome.kind === "failure") {
    return (
      <Section title="Visit plan" subtitle="Scheduling's plan for the facilities you cover.">
        <div className="fw-panel-content">
          <TideState kind="error" heading="The visit plan did not load" headingLevel={3} message={outcome.message}
            status={outcome.status} tone="danger" onRetry={load} />
        </div>
      </Section>
    );
  }

  const { data, selectedMissing } = outcome;
  const facilities = list(data.facilities);
  const upcoming = list(data.upcoming?.items);
  const completed = list(data.completed?.items);
  const sel = data.selected || null;
  const selOpen = list(sel?.open);
  const selDone = list(sel?.completed);
  const nameOf = (v: ScheduledVisit) => v.facility?.name || "Facility";

  return (
    <div className="fw-sched" aria-busy={loading || undefined}>
      {selectedMissing ? (
        <Section title="This facility is not on your schedule" subtitle="It is not one of the facilities you are assigned to cover.">
          <div className="fw-panel-content">
            <Notice>
              Your schedule across the facilities you cover is below. If you have started covering
              this facility, ask Scheduling to record the assignment.
            </Notice>
          </div>
        </Section>
      ) : sel ? (
        <Section
          title={`${sel.facility.name || "This facility"} · visit plan`}
          subtitle={sel.facility.closure
            ? `${closureSentence(sel.facility.closure)}.${sel.facility.closure.source === 'SCHEDULING_PROFILE' || !sel.facility.closure.source ? ' Scheduling plans no new visits here; an open visit below is one Scheduling still has to cancel or complete.' : ''}`
            : "Scheduling's plan in eMerge for the facility you have open."}
        >
          {selOpen.length ? (
            <ul className="fw-detail-list" aria-label="Open visits at this facility">
              {selOpen.map((v) => <VisitItem key={v.id} visit={v} />)}
            </ul>
          ) : (
            <div className="fw-panel-content">
              <p className="fw-muted">
                No visit is planned here yet. That usually means Scheduling has not put this facility
                on a rotation, rather than that a visit was missed.
              </p>
            </div>
          )}
          {selDone.length > 0 && (
            <>
              <h3 className="fw-sched-sub">Recently completed here</h3>
              <ul className="fw-detail-list" aria-label="Completed visits at this facility">
                {selDone.map((v) => <VisitItem key={v.id} visit={v} completed />)}
              </ul>
            </>
          )}
        </Section>
      ) : null}

      <Section
        title="Upcoming across your facilities"
        subtitle={data.enterprise && !facilities.length
          ? "Your access is not limited to particular facilities, so no cross-facility list is drawn."
          : `Planned and confirmed visits, today onward, at the ${facilities.length} ${facilities.length === 1 ? "facility" : "facilities"} you cover. Soonest first.`}
      >
        {upcoming.length ? (
          <>
            <Table aria-label="Upcoming visits across your facilities" className="fw-sched-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Facility</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead>Provider</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {upcoming.map((v) => (
                  <TableRow key={v.id} data-current={v.facility?.id === facilityId || undefined}>
                    <TableCell><When day={v.planned_on} /></TableCell>
                    <TableCell>
                      {v.facility?.id && v.facility.id !== facilityId ? (
                        <Button variant="link" className="fw-sched-open" onClick={() => onOpenFacility(v.facility.id)}
                          aria-label={`Open ${nameOf(v)}`}>
                          {nameOf(v)} <ArrowUpRight aria-hidden="true" />
                        </Button>
                      ) : (
                        <strong>{nameOf(v)}</strong>
                      )}
                      {v.facility?.city && <small className="fw-sched-city">{v.facility.city}</small>}
                      {v.building_closed && <StatusBadge tone="rose">{closureWords(v.building_closed)}</StatusBadge>}
                    </TableCell>
                    <TableCell><StatusBadge tone={STATE_TONE[v.state] || "neutral"}>{STATE_LABEL[v.state] || v.state}</StatusBadge></TableCell>
                    <TableCell>{v.purpose || <span className="fw-muted">Not recorded</span>}</TableCell>
                    <TableCell><Who visit={v} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data.upcoming.total > upcoming.length && (
              <p className="fw-muted fw-sched-more">
                Showing the next {upcoming.length} of {data.upcoming.total} planned visits.
              </p>
            )}
          </>
        ) : (
          <div className="fw-panel-content">
            <p className="fw-muted">No upcoming visit is planned at your facilities.</p>
          </div>
        )}
      </Section>

      {facilities.length > 0 && (
        <Section title="Your facilities" subtitle="Each facility you cover, with its next and last visit in eMerge.">
          <ul className="fw-sched-facilities" aria-label="Your facilities">
            {facilities.map((f) => (
              <li key={f.id} data-current={f.id === facilityId || undefined}>
                <div className="fw-sched-fac-head">
                  {f.id === facilityId ? (
                    <strong>{f.name || "Facility"}</strong>
                  ) : (
                    <Button variant="link" className="fw-sched-open" onClick={() => onOpenFacility(f.id)} aria-label={`Open ${f.name || "facility"}`}>
                      {f.name || "Facility"} <ArrowUpRight aria-hidden="true" />
                    </Button>
                  )}
                  {f.city && <small className="fw-sched-city">{[f.city, f.state].filter(Boolean).join(", ")}</small>}
                  {f.closure && <StatusBadge tone="rose">{closureWords(f.closure)}</StatusBadge>}
                </div>
                <dl>
                  <div>
                    <dt>Next visit</dt>
                    <dd>
                      {f.next_plan ? (
                        <>
                          {shortDate(f.next_plan.planned_on)}{" "}
                          <StatusBadge tone={f.next_plan.state === "CONFIRMED" ? "green" : "amber"}>
                            {f.next_plan.state === "CONFIRMED" ? "Confirmed" : "Planned"}
                          </StatusBadge>
                        </>
                      ) : <span className="fw-muted">None planned</span>}
                    </dd>
                  </div>
                  <div>
                    <dt>Last completed</dt>
                    <dd>
                      {f.last_completed?.completed_on
                        ? `${shortDate(f.last_completed.completed_on)}${f.last_completed.provider_name ? ` · ${f.last_completed.provider_name}` : ""}`
                        : <span className="fw-muted">None recorded in eMerge</span>}
                    </dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {completed.length > 0 && (
        <Section
          title={`Completed in the last ${data.completed.window?.days ?? 30} days`}
          subtitle="Visits Scheduling marked completed at your facilities, newest first."
        >
          <ul className="fw-detail-list" aria-label="Recently completed visits">
            {completed.map((v) => (
              <li key={v.id}>
                <CalendarCheck2 aria-hidden="true" />
                <div>
                  <div className="fw-sched-line">
                    <When day={v.completed_on} />
                    <strong>{nameOf(v)}</strong>
                  </div>
                  <p>{v.purpose || "No purpose recorded"} · <Who visit={v} /></p>
                </div>
              </li>
            ))}
          </ul>
          {data.completed.total > completed.length && (
            <p className="fw-muted fw-sched-more">Showing {completed.length} of {data.completed.total}.</p>
          )}
        </Section>
      )}

      <Notice>
        Visits are planned by Scheduling in eMerge. Nothing here is edited by a provider; ask
        Scheduling to move a visit. Completed is Scheduling&rsquo;s record that the visit happened,
        not the clinical record.
      </Notice>
    </div>
  );
}
